import { NetworkStatus } from '@apollo/client';
import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import defaultTo from 'lodash/defaultTo';
import merge from 'lodash/merge';
import uniqWith from 'lodash/uniqWith';
import { useCallback, useEffect } from 'react';

import type { OfferingFilterInput } from '../../../graphql/__generated__';
import type { Calendar_GridRowDataQueryVariables } from '../graphql/__generated__/Calendar_GridRowData';
import { useCalendar_GridRowDataLazyQuery } from '../graphql/__generated__/Calendar_GridRowData';

export type Props = {
  variables?: Pick<Calendar_GridRowDataQueryVariables, 'where'>;
};

const PAGE_SIZE = 200;
const MAX_RECORDS = 1000;

const DEFAULT_FILTERS: OfferingFilterInput = {
  attributes: {
    isSupersededBy: { eq: null },
  },
};
export const useGetCalendarGridRowData = ({ variables }: Props) => {
  const hasIoiPermission = useCheckPermissions([permissionsByEntity.Ioi.READ]);
  const hasCustomSectorsPermission = useCheckPermissions([permissionsByEntity.CustomSectors.READ]);

  const [fetchGridRows, { data, fetchMore, error, networkStatus }] =
    useCalendar_GridRowDataLazyQuery({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        where: merge(variables?.where, DEFAULT_FILTERS),
        skip: 0,
        take: PAGE_SIZE,
        hasIoiPermission,
        hasCustomSectorsPermission,
      },
    });

  const handleFetchMore = useCallback(async () => {
    await fetchMore({
      variables: {
        ...variables,
        skip: data?.offerings?.items?.length ?? 0,
        take: PAGE_SIZE,
      },
      updateQuery: (prev, curr) => {
        const { fetchMoreResult } = curr;

        /**
         * HasNextPage is always true when we call fetchMore.
         * However, it can change to false while fetching as the user can transition to another page.
         * That will refetch the original query above with different variables.
         */
        if (!fetchMoreResult.offerings?.items || !prev.offerings?.pageInfo.hasNextPage) {
          return prev;
        }

        return {
          offerings: {
            ...fetchMoreResult.offerings,
            items: uniqWith(
              [...(prev.offerings?.items ?? []), ...fetchMoreResult.offerings.items],
              (lhs, rhs) => lhs.id === rhs.id
            ),
          },
        };
      },
    });
  }, [data?.offerings?.items?.length, fetchMore, variables]);
  useEffect(() => {
    const isMaxRecordsReached = defaultTo(data?.offerings?.items?.length, 0) >= MAX_RECORDS;

    if (data?.offerings?.pageInfo.hasNextPage && !isMaxRecordsReached) {
      void handleFetchMore();
    }
  }, [data?.offerings?.items?.length, data?.offerings?.pageInfo.hasNextPage, handleFetchMore]);

  return [
    fetchGridRows,
    {
      rowData: data?.offerings?.items,
      error,
      isAllDataLoaded:
        data?.offerings &&
        !data.offerings.pageInfo.hasNextPage &&
        networkStatus === NetworkStatus.ready,
      totalRowCount: defaultTo(data?.offerings?.totalCount, null),
    },
  ] as const;
};
